import React, { PureComponent } from 'react';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { ExpertiseLevels } from 'Common/components/Organisms/ExpertiseLevels';
import { Modal } from 'Common/components/Molecules/Modal';
import { Text } from 'Common/components/Atoms/Text';
import { InputLabel } from '../ReviewerSignUpProfile';
import StepCard from '../../components/StepCard';
import { Theme } from '@mui/material/styles';
import withTheme from '@mui/styles/withTheme';
import { actions as signupsActions } from '../../reducers/signups';
import { actions as accountActions } from '../../reducers/Account';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch, Action } from 'redux';
import isEmpty from 'lodash/isEmpty';
import {
  removeSkillFromMap,
  prepareSkillsMapForStorage,
  modifySkillsMap,
  verifySkillsMap,
  SkillsMap,
} from '../../utils/skillsMapHelpers';
import { User, Skill, ReviewerSkill } from '../../models/User';
import { logEvent, REVIEWER_APPLICATION_CONFIDENCE } from 'utils/analytics';
import {
  accountUserSelector,
  isLoadingAccountSelector,
} from 'selectors/Account';

export interface Props {
  user: User;
  storeReviewerSkills: (
    skills: {
      [key: number]: ReviewerSkill;
    },
    complete: boolean
  ) => void;
  theme: Theme;
  error?: string;
  isUpdating: boolean;
  isLoading: boolean;
  previousStep: () => void;
}

interface State {
  skillsMap: SkillsMap;
  skillToRemove?: number;
  errors?: boolean;
}

export class ReviewerProfileExpertise extends PureComponent<Props, State> {
  state: State = {
    skillsMap: {},
    skillToRemove: undefined,
    errors: false,
  };

  componentDidMount() {
    this.setSkillsMap();
    logEvent(REVIEWER_APPLICATION_CONFIDENCE);
  }

  componentDidUpdate(prevProps: Props) {
    const { user } = this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};

    if (skills && prevProps.user?.reviewer_profile?.skills !== skills) {
      this.setSkillsMap();
    }
  }

  setSkillsMap = () => {
    const { user } = this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};
    if (user && skills && !isEmpty(skills)) {
      const skillsMap = skills.reduce((acc: SkillsMap, skill: Skill) => {
        acc[skill.id] = skill;
        return acc;
      }, {});

      this.setState({ skillsMap });
    }
  };

  handleChangeYearsExperience = (skillId: number, yearsExperience: number) => {
    const newSkillsMap = modifySkillsMap(
      this.state.skillsMap,
      skillId,
      'years_experience',
      yearsExperience
    );
    this.setState({
      skillsMap: newSkillsMap,
      errors: false,
    });
  };

  handleChangeSkillLevel = (skillId: number, skillLevel: number) => {
    const newSkillsMap = modifySkillsMap(
      this.state.skillsMap,
      skillId,
      'skill_level',
      skillLevel
    );
    this.setState({
      skillsMap: newSkillsMap,
      errors: false,
    });
  };

  handleSubmit = () => {
    const { storeReviewerSkills } = this.props;
    const { skillsMap } = this.state;
    if (!verifySkillsMap(skillsMap)) {
      this.setState({ errors: true });
      return;
    }

    const preparedSkillsMap = prepareSkillsMapForStorage(skillsMap);
    if (preparedSkillsMap) {
      storeReviewerSkills(preparedSkillsMap, true);
    }
  };

  setSkillToRemove = (skillId?: number) => {
    this.setState({ skillToRemove: skillId });
  };

  removeSkill = () => {
    const { skillsMap, skillToRemove } = this.state;
    if (skillToRemove === undefined) {
      return;
    }

    this.setState({
      skillsMap: removeSkillFromMap(skillsMap, skillToRemove),
      skillToRemove: undefined,
    });
  };

  getSkillById = (skillId?: number) => {
    const { user } = this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};
    if (!skillId || !skills || isEmpty(skills)) {
      return null;
    }
    return skills.find((skill) => {
      return skill.id === skillId;
    });
  };

  render(): JSX.Element {
    const { theme, error, isUpdating, isLoading, previousStep, user } =
      this.props;
    const { reviewer_profile } = user;
    const { skills } = reviewer_profile || {};
    const { skillsMap, skillToRemove, errors } = this.state;
    const skillRemoving = this.getSkillById(skillToRemove);

    return (
      <StepCard
        handleSubmit={this.handleSubmit}
        isUpdating={isUpdating}
        primaryButtonColor={theme.palette.success.main}
        primaryButtonText="Submit"
        previousStep={previousStep}
      >
        <InputLabel>Skills &amp; Experience</InputLabel>
        <Div mt={4}>
          {isLoading || isEmpty(skills) ? (
            <React.Fragment>
              <ContentLoader height="50px" width="100%" mb={2} />
              <ContentLoader height="50px" width="100%" mb={2} />
              <ContentLoader height="50px" width="100%" mb={2} />
            </React.Fragment>
          ) : (
            <React.Fragment>
              <ExpertiseLevels
                key={JSON.stringify(skillsMap)}
                skills={skillsMap}
                onChangeExperienceSelector={this.handleChangeYearsExperience}
                onChangeSkillLevel={this.handleChangeSkillLevel}
                onClickRemove={this.setSkillToRemove}
              />
              {errors && (
                <Text variant="caption" color="error" textAlign="center" mb={3}>
                  Please select skill level and years of experience for each of
                  your skills.
                </Text>
              )}
              {error && (
                <Text variant="caption" color="error" textAlign="center" mb={3}>
                  {error}
                </Text>
              )}
            </React.Fragment>
          )}
          <Modal
            variant="alert"
            isOpen={Boolean(skillToRemove)}
            showSecondaryButton={true}
            primaryButtonColor={theme.palette.highlight.main}
            primaryButtonLabel="Yes, remove"
            title="Removing skill"
            onClickPrimaryButton={this.removeSkill}
            onRequestClose={() => this.setSkillToRemove(undefined)}
          >
            <Div width="95%">
              Are you sure you want to remove the
              {` ${skillRemoving?.name} `} skill?
            </Div>
          </Modal>
        </Div>
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const user = accountUserSelector(state);
  const isLoading = isLoadingAccountSelector(state);

  return {
    isUpdating: signups.isUpdating,
    isLoading,
    user,
    error: signups.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default withTheme<Theme, React.JSXElementConstructor<Props>>(
  connect(mapStateToProps, mapDispatchToProps)(ReviewerProfileExpertise)
) as any;
